<template>
  <div id="app">
    <ImpersonateBar />
    <RouterView />
    <Transition name="captcha-overlay">
      <div v-if="showCaptchaOverlay" class="captcha-overlay">
        <div class="captcha-overlay__card">
          <div
            class="amplify-heading amplify-heading--3 captcha-overlay__title"
          >
            {{ $t('captcha.title') }}
          </div>
          <p class="captcha-overlay__subtitle">
            {{ $t('captcha.subtitle') }}
          </p>
          <div v-if="captchaLoading" class="captcha-overlay__spinner" />
          <div
            v-show="!captchaLoading"
            ref="captchaContainer"
            class="captcha-overlay__widget"
          />
          <p v-if="captchaFailed" class="captcha-overlay__error">
            {{ $t('captcha.error') }}
          </p>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script lang="ts">
import * as Sentry from '@sentry/vue';
import { Hub } from 'aws-amplify/utils';
import ImpersonateBar from '@/components/user-management/ImpersonateBar.vue';
import { defineComponent, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { LocationQueryValue, RouteLocationRaw } from 'vue-router';
import { rememberDevice } from 'aws-amplify/auth';
import useWafCaptcha, {
  registerCaptchaRequiredCallback,
  getCaptchaToken,
} from '@/composables/useWafCaptcha';

interface OrugaLoading {
  close: () => void;
}

export default defineComponent({
  components: {
    ImpersonateBar,
  },
  data() {
    return {
      loading: null as OrugaLoading | null,
    };
  },
  async created() {
    Hub.listen('auth', this.onAuth);

    this.$store.dispatch('auth/getUser');
  },
  methods: {
    async onAuth(data: any) {
      const { payload } = data;
      if (payload.event === 'signedIn') {
        // Open loader when signIn event is active
        this.loading = this.$oruga.loading.open({});

        await this.$store.dispatch('auth/getUser');

        // Remember the device if the option was set during login
        this.rememberDevice();

        // Get the profile
        await this.$store.dispatch('profile/getProfile');
        await this.redirectOnSignIn();
      }
      if (payload.event === 'signedOut') {
        await this.$store.dispatch('resetState');
        await this.$router.push('/auth');
      }
    },
    rememberDevice() {
      if (!this.$store.state.auth.rememberDevice) {
        return;
      }

      rememberDevice()
        .then()
        .catch((err: any) => {
          Sentry.captureException(err);
        });
    },
    onLocaleChange(newVal?: string): void {
      if (newVal) {
        document.documentElement.setAttribute('lang', newVal);
      }
    },
    async redirectOnSignIn() {
      // Close loader when profile is available and redirect to home
      if (this.loading) {
        this.loading.close();
      }

      const redirect: LocationQueryValue[] | LocationQueryValue =
        this.$route.query.redirect || '/dashboard';
      await this.$router.push(redirect as RouteLocationRaw);
    },
  },
  watch: {
    '$i18n.locale': [{ immediate: true, handler: 'onLocaleChange' }],
  },
  setup() {
    const { locale } = useI18n();
    const captchaContainer = ref<HTMLElement | null>(null);
    const {
      solved: captchaSolved,
      failed: captchaFailed,
      loading: captchaLoading,
    } = useWafCaptcha(captchaContainer, locale);

    const showCaptchaOverlay = ref(false);
    const captchaResolve = ref<((token: string) => void) | null>(null);

    function waitForCaptcha(): Promise<string> {
      const token = getCaptchaToken();
      if (captchaSolved.value && token) return Promise.resolve(token);
      showCaptchaOverlay.value = true;
      return new Promise<string>((resolve) => {
        if (captchaResolve.value) {
          const prev = captchaResolve.value;
          captchaResolve.value = (t: string) => {
            prev(t);
            resolve(t);
          };
        } else {
          captchaResolve.value = resolve;
        }
      });
    }

    registerCaptchaRequiredCallback(waitForCaptcha);

    watch(captchaSolved, (solved) => {
      if (solved && captchaResolve.value) {
        captchaResolve.value(getCaptchaToken() as string);
        captchaResolve.value = null;
        showCaptchaOverlay.value = false;
      }
    });

    return {
      captchaContainer,
      captchaFailed,
      captchaLoading,
      showCaptchaOverlay,
    };
  },
});
</script>

<style lang="scss">
.captcha-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 32, 64, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;

  &__card {
    background: white;
    border-radius: 8px;
    box-shadow: $card-box-shadow;
    padding: 35px 40px;
    max-width: 400px;
    width: 100%;
    margin: 0 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  &__title {
    margin-bottom: 8px;
    text-align: center;
  }

  &__subtitle {
    font-size: 14px;
    color: var(--amplify-colors-neutral-80);
    text-align: center;
    margin-bottom: 8px;
  }

  &__spinner {
    width: 40px;
    height: 40px;
    border: 3px solid rgba($primary, 0.2);
    border-top-color: $primary;
    border-radius: 50%;
    animation: captcha-spin 0.7s linear infinite;
    margin: 16px 0;
  }

  &__widget {
    width: 100%;
    min-height: 100px;
  }

  &__error {
    color: var(--amplify-colors-red-80);
    font-size: 14px;
    margin-top: 8px;
    text-align: center;
  }
}

@keyframes captcha-spin {
  to {
    transform: rotate(360deg);
  }
}

.captcha-overlay-leave-active {
  transition: opacity 0.3s ease;

  .captcha-overlay__card {
    transition: opacity 0.3s ease, transform 0.3s ease;
  }
}

.captcha-overlay-leave-to {
  opacity: 0;

  .captcha-overlay__card {
    opacity: 0;
    transform: translateY(-4px);
  }
}

.captcha-overlay-enter-active {
  transition: opacity 0.2s ease;
}

.captcha-overlay-enter-from {
  opacity: 0;
}
</style>
